/**
 * Deterministic date formatting for Prismic content dates.
 *
 * Every render-time date string must format identically on the server and in
 * every visitor's browser, or client components throw React #418 (hydration
 * mismatch) and server components risk it the moment they gain 'use client'.
 * `toLocaleDateString` without a pinned locale/timeZone is environment-defined
 * on both axes, so all content-date formatting routes through here.
 *
 * Prismic Date fields are date-only strings ("2025-06-12"), which `new Date`
 * parses as UTC midnight — pinning the output to UTC renders exactly the
 * calendar date the editor picked, with no off-by-one-day drift in
 * negative-offset timezones.
 */
export function formatPrismicDate(
  date: string | Date | null | undefined,
  locale: string = 'en-GB',
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}

/** The blog's `dd.mm.yyyy` date style, shared by post cards and detail pages. */
export function formatPrismicDateDotted(date: string | Date | null | undefined): string {
  return formatPrismicDate(date, 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).replace(/\//g, '.');
}
